import { Component, Prop, State, h } from '@stencil/core';
import SpecialDiscountIcon from '../../assets/svg/special-discount.svg';
import GiftIcon from '../../assets/svg/gift.svg';
import TagMoneyIcon from '../../assets/svg/tag-money.svg';
import FireIcon from '../../assets/svg/fire.svg';
import DiscountCouponIcon from '../../assets/svg/discount-coupon.svg';
import ArrowDownIcon from '../../assets/svg/keyboard_arrow_down.svg';

@Component({
  tag: 'salla-cart-item-offers',
  styleUrl: 'salla-cart-item-offers.scss',
})
export class SallaCartItemOffers {
  /**
   * The quantity of the cart item
   */
  @Prop() quantity!: number;
  /**
   * JSON string containing the offers associated with the cart item
   */
  @Prop() offers!: string;
  /**
   * The unique identifier of the cart item
   */
  @Prop() itemId!: number;
  /**
   * The original price of the product in the cart
   */
  @Prop() productPrice!: number;

  @State() internalQuantity!: number;
  @State() internalOffers: any[] = [];
  @State() internalProductPrice!: number;
  @State() showAll: boolean = false;
   // Translations
  @State() freeLabel: string = '';
  @State() receivedOffer: string = '';
  @State() discountAmountLabel: string = '';
  @State() showMoreOffers: string = '';


  private readonly icons: Record<string, string> = {
    DiscountCouponIcon,
    GiftIcon,
    FireIcon,
    SpecialDiscountIcon,
    TagMoneyIcon,
  };

  private visibleOffersCount = 3;

  async componentWillLoad() {
    await Salla.onReady();
    await salla.lang.onLoaded(() => {
        this.freeLabel = salla.lang.get('common.elements.freeExclam');
        this.receivedOffer = salla.lang.get('pages.cart.received_offer');
        this.discountAmountLabel = salla.lang.get('pages.cart.discount_amount');
        this.showMoreOffers = salla.lang.get('pages.cart.show_more_offers');
      });
    this.internalQuantity = this.quantity;
    this.internalOffers = this.parseOffers(this.offers);
    this.internalProductPrice = this.productPrice;
  }

  componentDidLoad() {
    salla.event.on('cart::item.updated', (event) => this.handleCartItemUpdated(event));
  }

  private parseOffers(offers: string): any[] {
    try {
      return JSON.parse(offers || '[]');
    } catch (e) {
      console.error('Failed to parse offers', e);
      return [];
    }
  }

  private handleCartItemUpdated = (event) => {
    const updatedItem = event.data.cart.items.find((item) => item.id === this.itemId);
    if (updatedItem.id === this.itemId) {
      this.internalQuantity = updatedItem.quantity;
      this.internalOffers = updatedItem.detailed_offers || [];
      this.internalProductPrice = updatedItem.product_price;
    }
  };

  private get visibleOffers() {
    return this.internalOffers.slice(0, this.visibleOffersCount);
  }

  private get hiddenOffers() {
    return this.internalOffers.slice(this.visibleOffersCount);
  }

  private toggleShowAll = () => {
    this.showAll = !this.showAll;
  };

  private getPaidQty(quantity: number, offer: any) {
    return quantity - offer.free_quantity;
  }

  private renderOffer(quantity: number, offer: any) {
    const paidQty = this.getPaidQty(quantity, offer);

    return (
      <div class="s-cart-item-offers-box">
        {offer.free_quantity > 0 && paidQty > 0 && (
          <div class="s-cart-item-offers-paid-free">
            <div class="s-cart-item-offers-line">
              {salla.helpers.number(paidQty)} × <span innerHTML={salla.money(this.internalProductPrice)}></span>
            </div>
            <div class="s-cart-item-offers-line">
              {salla.helpers.number(offer.free_quantity)} ×  
              <span class="s-cart-item-offers-regular-price" innerHTML={salla.money(this.internalProductPrice)}></span>
              <span class="s-cart-item-offers-free-label">{this.freeLabel}</span>
            </div>
          </div>
        )}

        <div class="s-cart-item-offers-details">
          <span class={`s-cart-item-offers-icon`} innerHTML={this.icons[offer.discount_icon] || ''}></span>
          <div>
            <p class="s-cart-item-offers-title">
              {this.receivedOffer.replace(':offer', offer.offer_name)}
            </p>
            <p class="s-cart-item-offers-discount">
              <span innerHTML={this.discountAmountLabel.replace(':amount', salla.money(offer.discount_amount.toFixed(2)))}></span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (!this.internalOffers?.length) {
      return null;
    }

    return (
      <div class="s-cart-item-offers-container" id={`offers_list_${this.itemId}`}>
        {this.visibleOffers.map((offer) => this.renderOffer(this.internalQuantity, offer))}

        {this.internalOffers?.length > this.visibleOffersCount && (
          <div class="s-cart-item-offers-show-more">
            <button
              class="s-cart-item-offers-show-more-btn"
              onClick={this.toggleShowAll}
              type="button">
              <span class="s-cart-item-offers-show-more-label">
                +{this.hiddenOffers.length} {this.showMoreOffers}
                <span
                  innerHTML={ArrowDownIcon}
                  class={{
                    's-cart-item-offers-arrow-icon': true,
                    's-cart-item-offers-arrow-open': this.showAll,
                  }}
                ></span>
              </span>
            </button>
          </div>
        )}

        {this.showAll && (
          <div class="s-cart-item-offers-collapsed" id={`offers_${this.itemId}`}>
            {this.hiddenOffers.map((offer) => this.renderOffer(this.internalQuantity, offer))}
          </div>
        )}
      </div>
    );
  }
}
